/**
 * @file: This file writes a list of playlist names into a sentence.
 *
 * The panel and the Preflight card both name the intervals that cover another
 * interval. The two texts must use the same words, so the rule is in one file.
 *
 * A playlist can have two intervals at the same stretch. A sentence names each
 * playlist once, so both functions read each name once.
 */

/** A base can lie under a takeover on each day of a week, so a sentence names this many and counts the rest. */
const NAMES_IN_A_SENTENCE = 3

/** Give each name once, in the order of its first occurrence. */
const distinct = names => [...new Set(names)]

/**
 * Join names for a sentence: "A", "A and B", "A, B and C", "A, B, C and 2 more".
 *
 * @param {string[]} allNames - The names in the order to read them. A name can occur twice
 * @returns {string} The names as one part of a sentence
 */
export function namesInProse (allNames) {
  const names = distinct(allNames)

  if (names.length > NAMES_IN_A_SENTENCE) {
    return `${names.slice(0, NAMES_IN_A_SENTENCE).join(', ')} and ${names.length - NAMES_IN_A_SENTENCE} more`
  }

  if (names.length < 2) {
    return names.join('')
  }

  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/**
 * The verb form that agrees with a list of names as the subject.
 *
 * @param {string[]} names - The subject of the sentence
 * @param {string} one - The verb for one name, for example "covers"
 * @param {string} many - The verb for more names, for example "cover"
 * @returns {string} One of the two forms
 */
export function verbFor (names, one, many) {
  return distinct(names).length === 1 ? one : many
}
